import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import FleetMap from "../components/FleetMap";
import { PageHeader } from "../components/Table";
import { Trash, Plus, MapPinLine } from "@phosphor-icons/react";

const CATEGORIES = ["landmark", "office", "warehouse", "customer", "plant", "fuel", "parking"];

export default function Pois() {
  const [pois, setPois] = useState([]);
  const [form, setForm] = useState({ name: "", category: "landmark", lat: "", lng: "", radius_m: "300" });

  const load = () => api.get("/pois").then((r) => setPois(r.data.pois));
  useEffect(() => { load(); }, []);

  const onMapClick = (lat, lng) => setForm((f) => ({ ...f, lat: lat.toFixed(5), lng: lng.toFixed(5) }));

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    if (!form.lat || !form.lng) return toast.error("Click a location on the map");
    await api.post("/pois", { name: form.name, category: form.category,
      lat: parseFloat(form.lat), lng: parseFloat(form.lng), radius_m: parseInt(form.radius_m) });
    toast.success("POI dropped"); setForm({ ...form, name: "", lat: "", lng: "" }); load();
  };
  const remove = async (id) => { await api.delete(`/pois/${id}`); toast.success("Deleted"); load(); };

  const draftMarker = form.lat && form.lng ? [{ id: "draft", vehicle_number: form.name || "New POI",
    lat: parseFloat(form.lat), lng: parseFloat(form.lng), speed: 0, status: "idle", vehicle_type: "Car" }] : [];

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="POI & Landmarks" subtitle="Drop named places — playback & reports show these instead of coordinates" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border rounded-sm overflow-hidden h-[520px] relative">
          <FleetMap vehicles={draftMarker} pois={pois} onMapClick={onMapClick} center={[19.08, 72.88]} zoom={11} />
          <div className="absolute top-2 left-2 z-[1000] bg-popover/90 border border-border rounded-sm px-3 py-1.5 text-xs flex items-center gap-2">
            <MapPinLine size={14} weight="bold" className="text-primary" />
            {form.lat ? `Selected: ${form.lat}, ${form.lng}` : "Click on the map to place a POI"}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-4 space-y-2">
            <div className="label-caps mb-1">New POI</div>
            <input data-testid="poi-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Place name" className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm" />
            <select data-testid="poi-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-background border border-border rounded-sm px-2 py-2 text-sm">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <input data-testid="poi-radius" value={form.radius_m} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} placeholder="Radius (m)" className="flex-1 bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <span className="text-xs text-muted-foreground">± {form.radius_m}m</span>
            </div>
            <button data-testid="poi-create" onClick={create} className="w-full flex items-center justify-center gap-1.5 bg-primary text-white font-semibold text-sm py-2 rounded-sm"><Plus size={15} weight="bold" /> Drop POI</button>
          </div>
          <div className="bg-card border border-border rounded-sm p-2 max-h-[260px] overflow-y-auto" data-testid="poi-list">
            {pois.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">No POIs yet</div>}
            {pois.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-2 py-2 border-b border-border/50 last:border-0 text-sm">
                <span><span className="font-semibold">{p.name}</span> <span className="text-xs text-muted-foreground">· {p.category} · {p.radius_m}m</span></span>
                <button data-testid={`poi-delete-${p.id}`} onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
